import type { OffsetPageInfo, PageInfo } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { SelectMenu } from './SelectMenu';

/**
 * The paging controls, matching the page shapes in the contract.
 *
 * None takes a "style" prop. Which control a screen gets is decided by which
 * page shape its endpoint returns (ADR-0009), not by what looks nice on that
 * screen — a list that pages by cursor cannot honestly offer a "page 3 of 12"
 * button, because the server has not counted and will not. OffsetPager and
 * OffsetPagerNumbered are the one exception to "one control per shape": both
 * render the identical OffsetPageInfo, just previous/next+status versus
 * literal page-number buttons — a rendering choice a screen makes, not a
 * paging-strategy choice.
 *
 * All are built to survive the 375px stacked-card layout: the buttons wrap
 * rather than overflow, and the status line sits on its own row below 480px.
 * Thai runs materially longer than English ("หน้า 1 จาก 6 · 137 รายการ"), so
 * the wrap point is set by the longest string, not the English one.
 */

/** Keyset paging: a "load more" button and an honest count of what is loaded. */
export function CursorPager({
  page,
  loaded,
  busy,
  onMore,
}: {
  page: PageInfo | null;
  /** Rows currently on screen. There is no total to show — see ADR-0009. */
  loaded: number;
  busy?: boolean;
  onMore: () => void;
}) {
  const { dict } = useLanguage();
  if (!page || loaded === 0) return null;

  return (
    <div className="pagination pagination--cursor">
      <span className="note pagination-status">{dict.common.showingSoFar(loaded)}</span>
      {page.has_more && (
        <button type="button" className="ghost" disabled={busy} onClick={onMore}>
          {busy ? dict.common.loading : dict.common.loadMore}
        </button>
      )}
    </div>
  );
}

/**
 * Keyset paging with page replace (prev/next) instead of append.
 * Still no total — status is "page N · showing M". Optional page-size picker
 * (10 / 20 / 50) for lists where the customer chooses how many rows to see.
 */
export function CursorPagePager({
  page,
  loaded,
  pageIndex,
  busy,
  onPrev,
  onNext,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  onPageSizeChange,
}: {
  page: PageInfo | null;
  loaded: number;
  /** 1-based page index within the cursor walk. */
  pageIndex: number;
  busy?: boolean;
  onPrev: () => void;
  onNext: () => void;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageSizeChange: (size: number) => void;
}) {
  const { dict } = useLanguage();
  if (!page || (loaded === 0 && pageIndex <= 1 && !busy)) return null;

  const totalPages =
    page.total != null && page.total > 0 ? Math.max(1, Math.ceil(page.total / pageSize)) : null;

  return (
    <div className="pagination pagination--cursor-pages">
      <div className="pagination-nav">
        <button
          type="button"
          className="ghost pagination-prev"
          disabled={busy || pageIndex <= 1}
          onClick={onPrev}
        >
          {dict.common.previous}
        </button>
        <span className="note pagination-status">
          {totalPages != null
            ? dict.common.pageOf(pageIndex, totalPages)
            : `${dict.common.pageNumber(pageIndex)} · ${dict.common.showingSoFar(loaded)}`}
        </span>
        <button
          type="button"
          className="ghost pagination-next"
          disabled={busy || !page.has_more}
          onClick={onNext}
        >
          {dict.common.next}
        </button>
      </div>
      <div className="pagination-size">
        <label htmlFor="cursor-page-size">{dict.common.rowsPerPage}</label>
        <SelectMenu
          id="cursor-page-size"
          aria-label={dict.common.rowsPerPage}
          value={String(pageSize)}
          onChange={(v) => onPageSizeChange(Number(v))}
          options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
        />
      </div>
    </div>
  );
}

/** Offset paging: previous/next, position within known total, and page-size picker. */
export function OffsetPager({
  page,
  busy,
  onPage,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  onPageSizeChange,
  /** When true, status is only "page N of M" — no item-count suffix. */
  compact,
}: {
  page: OffsetPageInfo | null;
  busy?: boolean;
  onPage: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  compact?: boolean;
}) {
  const { dict } = useLanguage();
  if (!page || page.total === 0) return null;

  return (
    <div className="pagination pagination--cursor-pages">
      <div className="pagination-nav">
        <button
          type="button"
          className="ghost pagination-prev"
          disabled={busy || page.page <= 1}
          onClick={() => onPage(page.page - 1)}
        >
          {dict.common.previous}
        </button>
        <span className="note pagination-status">
          {compact
            ? dict.common.pageOf(page.page, page.total_pages)
            : `${dict.common.pageOf(page.page, page.total_pages)} · ${dict.common.itemCount(page.total)}`}
        </span>
        <button
          type="button"
          className="ghost pagination-next"
          disabled={busy || !page.has_more}
          onClick={() => onPage(page.page + 1)}
        >
          {dict.common.next}
        </button>
      </div>
      {pageSize != null && onPageSizeChange && (
        <div className="pagination-size">
          <label htmlFor="offset-page-size">{dict.common.rowsPerPage}</label>
          <SelectMenu
            id="offset-page-size"
            aria-label={dict.common.rowsPerPage}
            value={String(pageSize)}
            onChange={(v) => onPageSizeChange(Number(v))}
            options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </div>
      )}
    </div>
  );
}

// pageWindow builds the numbered-button row: every page when there are few,
// otherwise 1 … current-1 current current+1 … last, so an account with a lot
// of history doesn't get a row of forty buttons. The categories this exists
// for (LLMR-76's security-history sections) are small per ADR-0009, so this
// rarely has to trim anything in practice.
function pageWindow(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | null)[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push(null);
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push(null);
  out.push(total);
  return out;
}

/**
 * Offset paging with literal clickable page-number buttons, for a screen
 * that wants "1 2 3" rather than OffsetPager's plain previous/next + status
 * line. Same page shape as OffsetPager (ADR-0009's small bounded
 * catalogues) — this is a different rendering of the identical page info,
 * not a different paging strategy.
 */
export function OffsetPagerNumbered({
  page,
  busy,
  onPage,
}: {
  page: OffsetPageInfo | null;
  busy?: boolean;
  onPage: (page: number) => void;
}) {
  const { dict } = useLanguage();
  if (!page || page.total === 0) return null;

  return (
    <div className="pagination pagination--numbered">
      <button
        type="button"
        className="ghost"
        disabled={busy || page.page <= 1}
        onClick={() => onPage(page.page - 1)}
      >
        {dict.common.previous}
      </button>
      <div className="pagination-pages">
        {pageWindow(page.page, page.total_pages).map((p, i) =>
          p === null ? (
            <span key={`ellipsis-${i}`} className="pagination-ellipsis" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={p === page.page ? 'pagination-page-btn is-current' : 'pagination-page-btn ghost'}
              disabled={busy}
              aria-current={p === page.page ? 'page' : undefined}
              aria-label={dict.common.goToPage(p)}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          ),
        )}
      </div>
      <button
        type="button"
        className="ghost"
        disabled={busy || !page.has_more}
        onClick={() => onPage(page.page + 1)}
      >
        {dict.common.next}
      </button>
    </div>
  );
}
